import {
  HeyLightOrderDTO,
  HeyLightOrderStatus,
} from "../../HeyLight/heyLightOrderDTO.ts";
import {
  OutlinedButton,
  PrimaryButton,
  TextInput,
} from "@normasteaching/norma-ui";
import { useRefundHeyLightOrder } from "./useRefundHeyLightOrder.ts";
import { useState } from "react";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useWithdrawalRequestDate } from "../../SpeakingClassService/Withdrawal/useWithdrawalRequestDate.ts";
import { WithdrawalRequestDateField } from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDateField.tsx";
import { ErrorPopup } from "../../UI/Error/ErrorPopup.tsx";
import { usePopupController } from "../../HeyLight/usePopupController.ts";
import { OrderStatusRenderer } from "./HeyLightOrderStatusRenderer.tsx";
import { OrderDetailsModal } from "./HeyLightOrderDetailsModal.tsx";
import { SplitActionButton } from "../../UI/SplitActionButton.tsx";
import { SortDirection } from "../../UI/Table/listUrlParams.ts";
import {
  OrderSortField,
  OrdersFilters,
  OrdersListQuery,
} from "../OrdersList/OrdersListQuery.ts";
import { OrdersTable, OrdersTableRow } from "../OrdersList/OrdersTable.tsx";

const STATUS_FILTER_OPTIONS = Object.values(HeyLightOrderStatus).map(
  (status) => ({
    value: status,
    label: <OrderStatusRenderer status={status} />,
  }),
);

function toHeyLightOrdersTableRow(
  order: HeyLightOrderDTO,
): OrdersTableRow<HeyLightOrderStatus> {
  return {
    id: order._id,
    createdAt: order.createdAt,
    customer: order.customerInformation,
    purchasedItem: {
      id: order.productId,
      type: order.productType,
      snapshot: order.productSnapshot,
    },
    chargedAmountInCents:
      order.amountInCent.amount - (order.coupon?.value ?? 0),
    couponCode: order.coupon?.couponCode,
    status:
      order.heyLightInformation?.heyLightOrderStatus ??
      HeyLightOrderStatus.Pending,
  };
}

type RefundModalData = {
  order: HeyLightOrderDTO;
  refundableAmount: number;
};

export function AdminHeyLightOrderTable({
  orders,
  readOnly,
  query,
  onSort,
  onFiltersChange,
}: {
  orders: HeyLightOrderDTO[];
  readOnly?: boolean;
  query: OrdersListQuery<HeyLightOrderStatus>;
  onSort: (field: OrderSortField, direction: SortDirection) => void;
  onFiltersChange: (filters: OrdersFilters<HeyLightOrderStatus>) => void;
}) {
  const [refundModalData, setRefundModalData] =
    useState<RefundModalData | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<HeyLightOrderDTO | null>(
    null,
  );
  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [partialAmount, setPartialAmount] = useState("");
  const { refundHeyLightOrder, loading: refundLoading } =
    useRefundHeyLightOrder();
  const { open, title, description, openPopup, closePopup } =
    usePopupController();
  const withdrawalDate = useWithdrawalRequestDate(
    refundModalData?.order.customerInformation.email,
  );

  const handleRefund = async () => {
    if (!refundModalData) return;
    try {
      if (refundType === "partial") {
        const amount = parseFloat(partialAmount);
        if (isNaN(amount) || amount <= 0) {
          openPopup({
            title: "Errore",
            description: "Inserisci un importo valido per il rimborso parziale",
            timeoutMs: 5000,
          });
          return;
        }
        if (amount > refundModalData.refundableAmount / 100) {
          openPopup({
            title: "Errore",
            description:
              "L'importo del rimborso non può superare l'importo rimborsabile",
            timeoutMs: 5000,
          });
          return;
        }
        await withdrawalDate.persist();
        await refundHeyLightOrder(
          refundModalData?.order._id,
          Math.round(amount * 100),
        );
      } else {
        await withdrawalDate.persist();
        await refundHeyLightOrder(refundModalData?.order._id);
      }

      setRefundModalData(null);
      setPartialAmount("");
      setRefundType("full");
    } catch (e) {
      const err = e as Error;
      openPopup({
        title: "Errore",
        description: `Si è verificato un errore durante il rimborso dell'ordine: ${err?.message ?? "Errore sconosciuto"}`,
        timeoutMs: 5000,
      });
    }
  };

  const openRefundModal = (order: HeyLightOrderDTO) => {
    setRefundModalData({
      order,
      refundableAmount: order.amountInCent.amount - (order.coupon?.value ?? 0),
    });
    setRefundType("full");
    setPartialAmount("");
  };

  return (
    <>
      <ErrorPopup
        description={description}
        title={title}
        onClose={closePopup}
        open={open}
      />

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      <Modal
        isOpen={!!refundModalData}
        onClose={() => {
          if (!refundLoading) {
            setRefundModalData(null);
            setPartialAmount("");
            setRefundType("full");
          }
        }}
        title="Seleziona il tipo di rimborso"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Scegli se effettuare un rimborso totale o parziale dell'ordine.
          </p>

          <label
            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
              refundType === "full"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200"
            }`}
          >
            <input
              type="radio"
              name="refundType"
              value="full"
              checked={refundType === "full"}
              onChange={() => setRefundType("full")}
              className="mt-1 w-4 h-4"
              disabled={refundLoading}
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Rimborso Totale</div>
              <div className="text-sm text-gray-600">
                Rimborsa l'intero importo rimborsabile
                {refundModalData && (
                  <span className="font-medium">
                    {" "}
                    (€{(refundModalData.refundableAmount / 100).toFixed(2)})
                  </span>
                )}
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
              refundType === "partial"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200"
            }`}
          >
            <input
              type="radio"
              name="refundType"
              value="partial"
              checked={refundType === "partial"}
              onChange={() => setRefundType("partial")}
              className="mt-1 w-4 h-4"
              disabled={refundLoading || !refundModalData?.order.isNewFlow}
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Rimborso Parziale</div>
              <div className="text-sm text-gray-600">
                Specifica l'importo da rimborsare
              </div>
            </div>
          </label>

          {refundType === "partial" && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Importo da rimborsare (€)
              </label>
              <TextInput
                type="number"
                placeholder="0.00"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                disabled={refundLoading}
                min={0}
              />
              {refundModalData && (
                <p className="text-xs text-gray-500 mt-1">
                  Massimo: €
                  {(refundModalData.refundableAmount / 100).toFixed(2)}
                </p>
              )}
            </div>
          )}

          <div className="mt-4">
            <WithdrawalRequestDateField
              date={withdrawalDate.date}
              onChange={withdrawalDate.setDate}
              existingRequest={withdrawalDate.existingRequest}
              hasWithdrawablePurchase={withdrawalDate.hasWithdrawablePurchase}
              loading={withdrawalDate.loading}
              disabled={refundLoading}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <PrimaryButton
              onClick={handleRefund}
              label="Conferma rimborso"
              loading={refundLoading}
              disabled={refundLoading}
            />
            <OutlinedButton
              onClick={() => {
                setRefundModalData(null);
                setPartialAmount("");
                setRefundType("full");
              }}
              label="Annulla"
              disabled={refundLoading}
            />
          </div>
        </div>
      </Modal>

      <OrdersTable
        orders={orders}
        toRow={toHeyLightOrdersTableRow}
        query={query}
        onSort={onSort}
        onFiltersChange={onFiltersChange}
        statusOptions={STATUS_FILTER_OPTIONS}
        renderStatus={(status) => <OrderStatusRenderer status={status} />}
        renderActions={
          readOnly
            ? undefined
            : (order) => (
                <SplitActionButton
                  mainAction={{
                    label: "Rimborsa",
                    tone: "neutral",
                    onClick: () => openRefundModal(order),
                    disabledReason:
                      order.heyLightInformation?.heyLightOrderStatus ===
                      HeyLightOrderStatus.Success
                        ? undefined
                        : "Il rimborso è possibile solo per gli ordini completati",
                  }}
                  otherActions={[
                    {
                      label: "Visualizza ordine",
                      onClick: () => setSelectedOrder(order),
                    },
                  ]}
                />
              )
        }
        onSelectOrder={setSelectedOrder}
      />
    </>
  );
}
